import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface JsonDiffModalData {
  from: string;
  to: string;
}

interface DiffRow {
  left: string;
  right: string;
  changed: boolean;
}

@Component({
  selector: 'app-json-diff-modal',
  templateUrl: './json-diff-modal.component.html',
  styleUrls: ['./json-diff-modal.component.scss']
})
export class JsonDiffModalComponent implements OnInit {
  rows: DiffRow[] = [];

  constructor(
    public dialogRef: MatDialogRef<JsonDiffModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: JsonDiffModalData
  ) {
  }

  ngOnInit(): void {
    const fromLines = this.prettyPrint(this.data?.from).split('\n');
    const toLines = this.prettyPrint(this.data?.to).split('\n');
    this.rows = this.buildDiff(fromLines, toLines);
  }

  exit(): void {
    this.dialogRef.close();
  }

  private prettyPrint(json: string): string {
    if (!json) {
      return '';
    }
    try {
      return JSON.stringify(this.expandNestedJson(JSON.parse(json)), null, 2);
    } catch {
      // если в поле лежит не валидный JSON - показываем как есть
      return json;
    }
  }

  // Рекурсивно разворачивает вложенные экранированные JSON-строки
  // (например, JsonRepresentation, Metadata), чтобы их содержимое
  // сравнивалось как структура, а не как одна длинная строка.
  private expandNestedJson(value: any): any {
    if (typeof value === 'string') {
      const parsed = this.tryParseJson(value);
      // разворачиваем только если строка действительно является JSON-объектом/массивом
      return parsed === undefined ? value : this.expandNestedJson(parsed);
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.expandNestedJson(item));
    }
    if (value !== null && typeof value === 'object') {
      const result: Record<string, any> = {};
      // сортируем ключи, чтобы одинаковые по содержанию объекты
      // не выглядели отличающимися из-за разного порядка полей
      for (const key of Object.keys(value).sort()) {
        result[key] = this.expandNestedJson(value[key]);
      }
      return result;
    }
    return value;
  }

  private tryParseJson(value: string): any {
    const trimmed = value.trim();
    // числа/булевы/null в виде строки не считаем вложенным JSON - сравниваем как строку
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return undefined;
    }
    try {
      return JSON.parse(trimmed);
    } catch {
      return undefined;
    }
  }

  // Построчное сравнение через LCS, чтобы вставки/удаления не сдвигали всю колонку.
  private buildDiff(fromLines: string[], toLines: string[]): DiffRow[] {
    const n = fromLines.length;
    const m = toLines.length;
    const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));

    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        lcs[i][j] = fromLines[i] === toLines[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
      }
    }

    const rows: DiffRow[] = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (fromLines[i] === toLines[j]) {
        rows.push({ left: fromLines[i], right: toLines[j], changed: false });
        i++;
        j++;
      } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
        rows.push({ left: fromLines[i], right: '', changed: true });
        i++;
      } else {
        rows.push({ left: '', right: toLines[j], changed: true });
        j++;
      }
    }
    while (i < n) {
      rows.push({ left: fromLines[i], right: '', changed: true });
      i++;
    }
    while (j < m) {
      rows.push({ left: '', right: toLines[j], changed: true });
      j++;
    }
    return rows;
  }
}
